import { useSnackbar } from 'notistack';
import { useMemo, useState, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { Grid, Alert, Stack, Avatar, Button, Dialog, Checkbox, TextField, Typography, DialogTitle, Autocomplete, DialogContent, FormControlLabel } from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { IJob } from 'src/services/jobs/jobs.types';
import { generateAddCandidateToJobError } from 'src/services/jobs/jobs.utils';
import { ApplicationStage } from 'src/services/applications/applications.types';
import { useGetOrganizationCandidateQuery } from 'src/services/candidates/candidates.service';
import { useGetOrganisationJobsQuery, useAddCandidateToJobMutation } from 'src/services/jobs/jobs.service';

import FeatureLockedChip from 'src/components/billing/feature-locked-chip';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  candidateId: string;
};

const ALLOWED_STAGES = [
  ApplicationStage.APPLIED,
  ApplicationStage.SCREENING
];

export default function AddCandidateJobModal({ candidateId, open, onClose }: Props) {
  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const tenantType = useOrgTenant();

  const router = useRouter();

  const { canAccess } = useFeatures();

  const [error, setError] = useState<string | null>(null);

  const [jobSearchQuery, setJobSearchQuery] = useState<string>('');

  const [defaultStage, setDefaultStage] = useState<ApplicationStage>(ApplicationStage.APPLIED);

  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [createInvite, setCreateInvite] = useState<boolean>(true);

  const { currentData: jobs } = useGetOrganisationJobsQuery({
    search_query: jobSearchQuery,
    page: 1,
    per_page: 5
  });

  const { currentData: orgCandidate, isLoading: isCandidateLoading } = useGetOrganizationCandidateQuery(candidateId, {
    skip: !candidateId
  });

  const [addCandidateToJob, { isLoading: isSubmitting }] = useAddCandidateToJobMutation();

  const canAccessInviteFeature = useMemo(() => canAccess(FeatureType.JOB_INVITES_ENABLED), [canAccess]);

  const onCloseDialog = useCallback(() => {

    setSelectedJob(null);
    onClose();
  }, [onClose]);

  const handleInviteCandidate = useCallback(async () => {
    try {

      if (!selectedJob || !orgCandidate) return;

      await addCandidateToJob({
        job_id: selectedJob.id as string,
        candidate_id: orgCandidate.id as string,
        create_invite: createInvite,
        default_stage: defaultStage
      }).unwrap();

      enqueueSnackbar(t('candidates.invites.add_to_job_modal.api.success'), { variant: 'success' });

      setSelectedJob(null);
      onCloseDialog();
    } catch (e) {
      setError(generateAddCandidateToJobError(e));
      enqueueSnackbar(generateAddCandidateToJobError(e), { variant: 'error' });
    }
  }, [addCandidateToJob, createInvite, defaultStage, enqueueSnackbar, onCloseDialog, orgCandidate, selectedJob, t]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onCloseDialog}>
      <DialogTitle>{t(`candidates.invites.add_to_job_modal.title`)}</DialogTitle>
      <DialogContent>
        {!!error && <Alert severity="error">{error}</Alert>}
        <Grid container spacing={2} justifyContent="center" pt={1}>
          <Grid item xs={12}>
            <Alert variant="outlined" severity="info">
              {t(`candidates.invites.add_to_job_modal.alert`)}
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" gap={1} justifyContent="start" alignItems="center" my={3} px={1}>
              <Avatar variant="rounded"
                src={orgCandidate?.candidate_profile.profile_image?.public_path as string}
                alt={orgCandidate?.candidate_profile.first_name}
              />
              <Stack direction="column">
                <Typography variant="h6">
                  {orgCandidate?.candidate_profile.first_name} {orgCandidate?.candidate_profile.last_name}
                </Typography>
                <Typography variant="caption">
                  {orgCandidate?.candidate_profile.position_title}
                </Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} >
            <Autocomplete
              options={jobs?.results as IJob[] ?? []}
              value={selectedJob}
              disabled={!canAccessInviteFeature}
              onInputChange={(_event, value) => setJobSearchQuery(value)}
              onChange={(_event, value) => setSelectedJob(value)}
              renderOption={({ key, ...props }, option: IJob) => (
                <li key={key} {...props}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.title}`}
                    </Typography>

                    {
                      tenantType === TenantType.Recruiter && (
                        <Typography variant="caption">{option.client_company_name}</Typography>
                      )
                    }

                  </Stack>
                </li>
              )}
              getOptionLabel={(option) =>
                option ? `${option.title}` : ''
              }
              isOptionEqualToValue={(option: IJob, value: IJob) => option.id === value?.id}
              getOptionKey={(option) => (option as IJob).id as string}
              renderInput={(params) => (
                <TextField {...params} label={t('jobs.toolbar.search.placeholder')} />
              )}
              sx={{
                minWidth: 200,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              options={ALLOWED_STAGES}
              value={defaultStage}
              onChange={(_event, value) => setDefaultStage(value as ApplicationStage)}
              renderOption={({ key, ...props }, option: ApplicationStage) => (
                <li key={key} {...props}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${t(`enums.application_stage.${option}`)}`}
                    </Typography>
                  </Stack>
                </li>
              )}
              getOptionLabel={(option) =>
                option ? `${t(`enums.application_stage.${option}`)}` : ''
              }
              isOptionEqualToValue={(option: ApplicationStage, value: ApplicationStage) => option === value}
              getOptionKey={(option) => (option as ApplicationStage)}
              renderInput={(params) => (
                <TextField {...params} label={t('candidates.invites.add_to_job_modal.default_stage.label')} />
              )}
              sx={{
                minWidth: 200,
              }}
            />
          </Grid>

          <Grid item xs={12} mb={5} >
            <FormControlLabel
              sx={{ pl: 1, alignItems: 'start' }}
              labelPlacement='end'
              label={
                <Stack direction="column">
                  <Typography variant="subtitle2">{t('candidates.invites.add_to_job_modal.create_invite.label')}</Typography>
                  <Typography variant="caption">{t('candidates.invites.add_to_job_modal.create_invite.subtitle')}</Typography>
                </Stack>
              }
              control={
                <Checkbox
                  checked={createInvite}
                  onChange={(e) => setCreateInvite(e.target.checked)}
                  color="primary"
                />
              }
            />
          </Grid>

          <Stack sx={{ py: 2, width: '100%' }} gap={1} flexDirection="row" justifyContent="flex-end" alignItems="center">

            <Button variant='text' onClick={onCloseDialog}>{t('common.cancel')}</Button>

            {
              canAccessInviteFeature ? (
                <LoadingButton
                  type="submit"
                  variant="contained"
                  onClick={handleInviteCandidate}
                  disabled={isSubmitting || !selectedJob || !orgCandidate}
                  loading={isSubmitting}
                >
                  {t('candidates.add_to_job')}
                </LoadingButton>
              ) : (
                <FeatureLockedChip text={t('common.upgrade')} />
              )
            }

          </Stack>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
